import React, { useCallback, useEffect, useState } from 'react';

import {
  appTokenAuthContext,
  clearAppIdToken,
  getAppIdTokenFromSessionStorage,
  getAppTokenUserId,
  isExpiredJwt,
  isWellFormedJwt,
} from 'microsites/partner-linking/app-token';
import LinkingError from 'microsites/partner-linking/linking-error';
import LinkingLoading from 'microsites/partner-linking/linking-loading';
import PartnerLinkingAuthGate from 'microsites/partner-linking/partner-linking-auth-gate';
import { consumePkce } from 'microsites/partner-linking/pkce';
import { useBackToApp } from 'microsites/partner-linking/use-back-to-app';
import { useIntl } from 'react-intl';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Loggable } from '@rbi-ctg/frontend';
import { PartnerLinkSource, useCompleteLinkingMutation } from 'generated/graphql-gateway';
import { useLocale } from 'state/intl';
import { StatusType } from 'utils/datadog';
import { routes } from 'utils/routing';

import {
  PartnerLinkingErrorReason,
  isCtAccountError,
  isEmailVerificationRequiredError,
  isRegionNotEligibleError,
} from '../errors';

import { makeCtLinkingLogger } from './log-linking';
import RegionUnavailable from './region-unavailable';
import VerificationRequired from './verification-required';

const logCompleteLinking = makeCtLinkingLogger('complete-linking');

/** Terminal callback outcomes that get a specific screen, not the generic error. */
type BlockedView = 'regionUnavailable' | 'verificationRequired' | 'ctAccountError';

/**
 * Pulls the Apollo code and CT diagnostic extensions (set by the loyalty
 * service's `mapCtErrorToApollo`) off a thrown `completeLinking` error, so a
 * failure such as `CT_UNAVAILABLE` is logged with full context rather than just
 * the user-facing message.
 */
function ctErrorContext(err: unknown): Record<string, Loggable> {
  const extensions =
    (err as { graphQLErrors?: ReadonlyArray<{ extensions?: Record<string, unknown> }> })
      ?.graphQLErrors?.[0]?.extensions ?? {};
  return {
    errorMessage: err instanceof Error ? err.message : String(err),
    apolloCode: typeof extensions.code === 'string' ? extensions.code : undefined,
    ctErrCode: typeof extensions.ctErrCode === 'string' ? extensions.ctErrCode : undefined,
    ctStatusCode: typeof extensions.ctStatusCode === 'number' ? extensions.ctStatusCode : undefined,
    ctErrorReason:
      typeof extensions.ctErrorReason === 'string' ? extensions.ctErrorReason : undefined,
    httpStatus: typeof extensions.httpStatus === 'number' ? extensions.httpStatus : undefined,
  };
}

/**
 * OIDC callback target for the CT partnership-linking flow, mounted at
 * `/partner-linking/canadian-tire/complete-linking`.
 *
 * The redirect from CT carries `?code=<auth code>&state=<our state>`. We:
 *   1. Recover `{codeVerifier, source}` from sessionStorage by `state`
 *      (single-use; entry is removed on read). If missing, CSRF defense
 *      tripped — refuse to call the gateway.
 *   2. Call `completeLinking(partnerId, source, authCode, codeVerifier)`.
 *   3. On success, navigate to the locale-appropriate Triangle success page.
 *      On failure, render the localized error screen.
 */
function CompleteLinkingInner() {
  const [searchParams] = useSearchParams();
  const [completeLinking] = useCompleteLinkingMutation();
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  // Persisted to localStorage at initiate time, so it survives the OIDC
  // round-trip and picks the right Triangle success page here.
  const { language } = useLocale();
  // The backend re-checks both gates here, so a guest who became ineligible
  // mid-flow gets the matching block screen rather than the generic error page.
  // The origin is only recovered from the PKCE entry inside `run`, so it is held
  // in state for the screen's CTA.
  const [blockedView, setBlockedView] = useState<BlockedView | null>(null);
  const [blockAppOrigin, setBlockAppOrigin] = useState(false);
  // Captured at block time because `showBlock` clears storage before the
  // verification screen renders.
  const [blockAppToken, setBlockAppToken] = useState<string | null>(null);
  const handleBackToApp = useBackToApp(blockAppOrigin);

  // `isAppOrigin` defaults to false (TH web home) so the error page's CTA is safe
  // on desktop, where a deep link would no-op. Callers that have recovered an
  // explicit app origin pass true.
  const goToError = useCallback(
    (reason: PartnerLinkingErrorReason, detail?: string, isAppOrigin = false) => {
      // The flow ends on the error page, so drop the handoff token.
      clearAppIdToken();
      navigate(routes.partnerLinkingError, {
        replace: true,
        state: { reason, detail, isAppOrigin },
      });
    },
    [navigate]
  );

  // Terminal with no LINKED record persisted, so the handoff token is dropped
  // exactly as the error path does.
  const showBlock = useCallback(
    (view: BlockedView, isAppOrigin: boolean, appToken: string | null) => {
      clearAppIdToken();
      setBlockAppOrigin(isAppOrigin);
      setBlockAppToken(appToken);
      setBlockedView(view);
    },
    []
  );

  const run = useCallback(async () => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const oauthError = searchParams.get('error');

    // App-origin guests have no Cognito session, so the generic logger's `userId`
    // is empty for them. Recover the Cognito `sub` from the handoff token to keep
    // failures attributable. `undefined` for web-origin, where the logger already
    // fills it in.
    const appToken = getAppIdTokenFromSessionStorage();
    const userId = getAppTokenUserId(appToken);
    const log = (event: string, status: StatusType, context: Record<string, Loggable> = {}): void =>
      logCompleteLinking(event, status, userId ? { userId, ...context } : context);

    if (oauthError) {
      log('partner reported oauth error on callback', StatusType.error, {
        oauthError,
        oauthErrorDescription: searchParams.get('error_description') ?? undefined,
      });
      goToError(
        PartnerLinkingErrorReason.PartnerReported,
        `${oauthError}${
          searchParams.get('error_description') ? `: ${searchParams.get('error_description')}` : ''
        }`
      );
      return;
    }

    if (!code || !state) {
      log('missing code or state on callback', StatusType.error, {
        codePresent: code ? 'yes' : 'no',
        statePresent: state ? 'yes' : 'no',
      });
      goToError(
        PartnerLinkingErrorReason.MissingCodeOrState,
        'expected ?code= and ?state= on callback URL'
      );
      return;
    }

    const stored = consumePkce(state);
    if (!stored) {
      // Either CSRF (state mismatch) or the user landed here in a tab that
      // didn't kick off the flow. Either way, refuse to proceed.
      log('pkce session expired — no verifier for returned state', StatusType.error, { state });
      goToError(
        PartnerLinkingErrorReason.SessionExpired,
        'no pkce verifier found for returned state'
      );
      return;
    }

    // Recovered the origin: only an explicit native-app entry returns to the
    // app; everything else (incl. web) goes to the TH web home.
    const isAppOrigin = stored.source === PartnerLinkSource.THIAPP;

    if (appToken !== null && !isWellFormedJwt(appToken)) {
      log('malformed app-handoff token on callback', StatusType.error, {
        source: stored.source,
      });
      goToError(
        PartnerLinkingErrorReason.CallbackMalformedToken,
        'malformed app-handoff token',
        isAppOrigin
      );
      return;
    }

    // The round-trip through the partner can outlast the handoff token. Refuse
    // rather than let the mutation fall back to the browser's session, which may
    // be a different guest than the one the app handed off.
    if (appToken !== null && isExpiredJwt(appToken)) {
      log('expired app-handoff token on callback', StatusType.error, {
        source: stored.source,
      });
      goToError(
        PartnerLinkingErrorReason.CallbackExpiredToken,
        'expired app-handoff token',
        isAppOrigin
      );
      return;
    }

    try {
      const result = await completeLinking({
        variables: {
          partnerId: 'CT',
          source: stored.source as PartnerLinkSource,
          authCode: code,
          codeVerifier: stored.codeVerifier,
        },
        context: appToken ? appTokenAuthContext(appToken) : undefined,
      });
      if (result.data?.completeLinking?.success) {
        log('linked successfully', StatusType.info, { source: stored.source });
        // The Triangle success pages are Sanity static pages rendered by the main
        // app shell, not microsite routes.
        clearAppIdToken();
        navigate(
          language === 'fr'
            ? routes.partnerLinkingCanadianTireSuccessFr
            : routes.partnerLinkingCanadianTireSuccess,
          { replace: true }
        );
        return;
      }
      log('completeLinking returned success=false', StatusType.error, {
        source: stored.source,
      });
      goToError(
        PartnerLinkingErrorReason.CompleteUnsuccessful,
        'completeLinking returned success=false',
        isAppOrigin
      );
    } catch (err) {
      if (isRegionNotEligibleError(err)) {
        log('region blocked on callback — ineligible region', StatusType.info, {
          source: stored.source,
        });
        showBlock('regionUnavailable', isAppOrigin, appToken);
        return;
      }
      if (isEmailVerificationRequiredError(err)) {
        // Verification lapsed between initiation and callback, or the initiate
        // gate was bypassed.
        log('email unverified on callback — refused by completeLinking', StatusType.info, {
          source: stored.source,
        });
        showBlock('verificationRequired', isAppOrigin, appToken);
        return;
      }
      // THLE-4387: CT rejected the account itself (inactive, already bound to
      // another Triangle account, loyalty-backend failure). Retrying can't fix
      // any of them, so the guest gets the "contact Triangle" screen rather
      // than the generic "go back and try again" page.
      if (isCtAccountError(err)) {
        log('CT account error on callback — Triangle error screen shown', StatusType.error, {
          source: stored.source,
          ...ctErrorContext(err),
        });
        showBlock('ctAccountError', isAppOrigin, appToken);
        return;
      }
      log('completeLinking call failed', StatusType.error, {
        source: stored.source,
        ...ctErrorContext(err),
      });
      goToError(
        PartnerLinkingErrorReason.CompleteError,
        err instanceof Error ? err.message : String(err),
        isAppOrigin
      );
    }
  }, [searchParams, completeLinking, navigate, goToError, showBlock, language]);

  useEffect(() => {
    void run();
  }, [run]);

  if (blockedView === 'regionUnavailable') {
    return <RegionUnavailable onBackToApp={handleBackToApp} />;
  }

  if (blockedView === 'verificationRequired') {
    return <VerificationRequired onBackToApp={handleBackToApp} idToken={blockAppToken} />;
  }

  if (blockedView === 'ctAccountError') {
    return (
      <LinkingError
        title={formatMessage({ id: 'ctLinkingAccountErrorTitle' })}
        message={formatMessage({ id: 'ctLinkingAccountErrorDescription' })}
        onBack={handleBackToApp}
      />
    );
  }

  return <LinkingLoading />;
}

export default function CanadianTireCompleteLinking() {
  return (
    <PartnerLinkingAuthGate>
      <CompleteLinkingInner />
    </PartnerLinkingAuthGate>
  );
}
